import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/types'
import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import { settings } from './settings'

const { APP_ENV, SENTRY_DSN } = settings

/**
 * Erros de domínio que representam comportamento esperado da aplicação.
 * Estes erros são filtrados no beforeSend e não são enviados ao Sentry.
 */
export const IGNORED_ERROR_NAMES = [
  'BadRequestError',
  'ConflictError',
  'ForbiddenError',
  'InvalidCredentialsError',
  'NotFoundError',
  'TrackingError',
  'UnexpectedError'
]

/**
 * Mensagens de erro genéricas/externas que devem ser ignoradas.
 */
const IGNORED_ERROR_MESSAGES = [
  /O conteúdo que você está tentando acessar não está disponível/i,
  'Network Error',
  'Importing a module script failed'
]

export const isIgnoredError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return IGNORED_ERROR_NAMES.includes(error.name)
  }

  if (typeof error === 'object' && error !== null && 'name' in error) {
    return IGNORED_ERROR_NAMES.includes((error as { name: string }).name)
  }

  return false
}

export default {
  dsn: SENTRY_DSN,
  environment: APP_ENV,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration()
  ],
  ignoreErrors: IGNORED_ERROR_MESSAGES,
  denyUrls: [/user-guiding.*\.js/i, /userguiding/i],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.1,
  beforeSend(event: ErrorEvent, hint: EventHint) {
    if (isIgnoredError(hint.originalException)) {
      return null
    }
    return event
  }
}
